import { useEffect, useState } from 'react'
import { fetchSkillEvidenceCases } from '../api/team'
import CaseBadge from './CaseBadge'
import styles from './SkillEvidence.module.css'

/**
 * SkillEvidence — the evidence-case list for a single skill, shown inline in the
 * AgentSkillsModal review variant.
 *
 * Self-fetching (like CaseEvidence): loads GET /skills/evidence/{agentId}/{skillId}
 * on mount and renders each ticket as a CaseBadge. Clicking a badge opens the full
 * per-case evidence modal (handled by CaseBadge → CaseEvidence).
 *
 * Props:
 *   agentId   {number|string} — agent id, passed to the API
 *   skillId   {number|string} — skill id, passed to the API
 *   skillName {string}        — this row's skill name; forwarded to each CaseBadge
 *                               as the primary skill to auto-select in the modal
 */
export default function SkillEvidence({ agentId, skillId, skillName }) {
  const [cases, setCases] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // `active` guards against a late response after the row is collapsed/unmounted.
  useEffect(() => {
    let active = true
    fetchSkillEvidenceCases(agentId, skillId)
      .then(d => { if (active) setCases(d.evidence ?? []) })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [agentId, skillId])

  if (loading) return <span className={styles.muted}>Loading evidence…</span>
  if (error) return <span className={styles.error}>Failed to load evidence: {error}</span>
  if (!cases.length) return <span className={styles.muted}>No evidence cases.</span>

  return (
    <div className={styles.cases}>
      <span className={styles.label}>Evidence cases</span>
      {cases.map(c => <CaseBadge key={c} caseId={c} primarySkill={skillName} />)}
    </div>
  )
}
